import { storeToRefs } from "pinia";
import LxcError from "~/core/models/LxcError.class";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import sectorsService from "~/modules/sector/services/sectors.service";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";

export function useFetchSectors(organizationCode?: number) {
  const fetchSectorsOrganizationCode = ref(organizationCode);

  const { currentOrganization } = useOrganizationStore();

  const { sectors } = storeToRefs(useSectorStore());

  const { setSectors } = useSectorStore();

  const isLoading: Ref<boolean> = ref(false);
  const error: Ref<LxcError | undefined> = ref();
  const sector: Ref<Sector | undefined> = ref();

  function setOrganizationCode(organizationCode?: number) {
    fetchSectorsOrganizationCode.value = organizationCode;
  }

  async function fetchSectors(refresh?: boolean) {
    isLoading.value = true;

    // Execute the fetch if:
    //  - the organization to fetch is different from the current one
    //  - or the organization to fetch is the same as the current one and the sectors store is not defined or need to be refreshed
    if (
      currentOrganization?.code !== fetchSectorsOrganizationCode.value ||
      (currentOrganization?.code === fetchSectorsOrganizationCode.value &&
        (!sectors.value || refresh))
    ) {
      const response = await sectorsService.getSectors({
        organizationCode: fetchSectorsOrganizationCode.value,
      });

      if (LxcError.check(response)) {
        error.value = response;
      } else {
        sector.value = response;
      }
    }

    // Refresh the sectors store if the organization to fetch is the same as the current one and the sectors store is not defined or need to be refreshed
    if (
      currentOrganization?.code === fetchSectorsOrganizationCode.value &&
      (!sectors.value || refresh)
    ) {
      setSectors(sector.value);
    }

    isLoading.value = false;
  }

  return {
    isLoading,
    error,
    sector,
    setOrganizationCode,
    fetchSectors,
  };
}
